import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Fade,
  Alert,
  Chip,
  Tooltip,
  Stack,
  Divider,
  Portal
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
import { Wallet } from 'ethers';
import _ from 'lodash';

// 钱包类型定义
export interface WalletConfig {
  privateKey: string;
  address: string;
  bnbBalance?: string;
  usdtBalance?: string;
  token1Balance?: string;
  token2Balance?: string;
  nonce?: number;
  status?: 'pending' | 'success' | 'error';
  errorMessage?: string;
}

// 组件属性类型定义
export interface WalletManagerMUIProps {
  wallets: WalletConfig[];
  selectedWalletIndices: number[];
  token1Symbol?: string;
  token2Symbol?: string;
  onImport: (wallets: WalletConfig[]) => void;
  onDelete: (indices: number[]) => void;
  onCheckBalance: (indices: number[]) => void;
  onSelectWallets: (wallets: WalletConfig[]) => void;
  onClearAll: () => void;
}

// 导入钱包的弹出层组件
interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (wallets: WalletConfig[]) => void;
}

const ImportModal: React.FC<ImportModalProps> = ({ open, onClose, onImport }) => {
  const [privateKeys, setPrivateKeys] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // 处理导入按钮点击
  const handleImportClick = async () => {
    try {
      setIsProcessing(true);
      setError('');

      // 分割多行私钥
      const keys = privateKeys.split('\n').map(key => key.trim()).filter(key => key);
      
      // 验证私钥格式
      const validKeys: string[] = [];
      for (const key of keys) {
        // 移除可能的前缀
        let cleanKey = key;
        if (cleanKey.startsWith('0x')) {
          cleanKey = cleanKey.substring(2);
        }
        
        // 检查是否是有效的私钥格式（64个十六进制字符）
        if (/^[0-9a-fA-F]{64}$/.test(cleanKey)) {
          validKeys.push(`0x${cleanKey}`);
        }
      }

      if (validKeys.length === 0) {
        setError('未找到有效的私钥格式');
        setIsProcessing(false);
        return;
      }

      // 创建钱包配置对象
      const wallets: WalletConfig[] = await Promise.all(validKeys.map(async (key) => {
        try {
          // 使用ethers.js从私钥创建钱包实例
          const wallet = new Wallet(key);
          return {
            privateKey: key,
            address: wallet.address
          };
        } catch (error) {
          console.error('从私钥创建钱包失败:', error);
          return {
            privateKey: key,
            address: '无效的私钥'
          };
        }
      }));

      // 导入钱包
      onImport(wallets);
      
      // 清空并关闭弹窗
      setPrivateKeys('');
      setIsProcessing(false);
      onClose();
    } catch (err) {
      console.error('处理私钥时出错:', err);
      setError('处理私钥时出错');
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setPrivateKeys('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>导入钱包私钥</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          请输入您的钱包私钥，每行一个。您的私钥将仅在本地处理，不会上传到任何服务器。
        </DialogContentText>
        <TextField
          autoFocus
          multiline
          rows={5}
          fullWidth
          variant="outlined"
          placeholder="请在此粘贴私钥，每行一个"
          value={privateKeys}
          onChange={(e) => setPrivateKeys(e.target.value)}
          disabled={isProcessing}
          error={!!error}
          helperText={error}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          取消
        </Button>
        <Button 
          onClick={handleImportClick} 
          variant="contained" 
          disabled={isProcessing}
        >
          {isProcessing ? '处理中...' : '导入'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// 钱包管理组件
const WalletManagerMUI: React.FC<WalletManagerMUIProps> = ({
  wallets,
  selectedWalletIndices,
  token1Symbol,
  token2Symbol,
  onImport,
  onDelete,
  onCheckBalance,
  onSelectWallets,
  onClearAll
}) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // 处理钱包选择
  const handleWalletSelection = (index: number) => {
    const isSelected = selectedWalletIndices.includes(index);
    
    if (isSelected) {
      // 如果已选中，从选中列表中移除
      const newIndices = selectedWalletIndices.filter(i => i !== index);
      const newSelectedWallets = newIndices.map(i => wallets[i]);
      onSelectWallets(newSelectedWallets);
    } else {
      // 如果未选中，添加到选中列表中
      const newIndices = [...selectedWalletIndices, index];
      const newSelectedWallets = newIndices.map(i => wallets[i]);
      onSelectWallets(newSelectedWallets);
    }
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedWalletIndices.length === wallets.length) {
      onSelectWallets([]);
    } else {
      onSelectWallets(wallets);
    }
  };

  // 选择有余额的钱包
  const selectWithBalance = () => {
    const walletsWithBalance = wallets
      .filter(wallet => wallet.bnbBalance && parseFloat(wallet.bnbBalance) > 0);
    
    onSelectWallets(walletsWithBalance);
    
    const message = walletsWithBalance.length > 0 
      ? `已选择 ${walletsWithBalance.length} 个有余额的钱包` 
      : '没有找到有余额的钱包';
    
    setSnackbar({
      open: true,
      message,
      severity: walletsWithBalance.length > 0 ? 'success' : 'info'
    });
  };

  // 选择零余额钱包
  const selectZeroBalance = () => {
    const zeroBalanceWallets = wallets
      .filter(wallet => 
        wallet.bnbBalance !== undefined && 
        parseFloat(wallet.bnbBalance) === 0
      );
    
    onSelectWallets(zeroBalanceWallets);
    
    const message = zeroBalanceWallets.length > 0 
      ? `已选择 ${zeroBalanceWallets.length} 个零余额钱包` 
      : '没有找到零余额钱包';
    
    setSnackbar({
      open: true,
      message,
      severity: zeroBalanceWallets.length > 0 ? 'success' : 'info'
    });
  };

  // 选择错误状态的钱包
  const selectErrorWallets = () => {
    const errorWallets = wallets
      .filter(wallet => wallet.status === 'error');
    
    onSelectWallets(errorWallets);
    
    const message = errorWallets.length > 0 
      ? `已选择 ${errorWallets.length} 个错误状态的钱包` 
      : '没有找到错误状态的钱包';
    
    setSnackbar({
      open: true,
      message,
      severity: errorWallets.length > 0 ? 'success' : 'info'
    });
  };

  // 删除选中的钱包
  const handleDelete = () => {
    if (selectedWalletIndices.length === 0) {
      setSnackbar({
        open: true,
        message: '请先选择要删除的钱包',
        severity: 'warning'
      });
      return;
    }
    
    // 获取选中钱包的索引
    const indicesToDelete = selectedWalletIndices;
    
    onDelete(indicesToDelete);
  };

  // 导出钱包地址到文件
  const exportWalletAddresses = () => {
    if (wallets.length === 0) {
      setSnackbar({
        open: true,
        message: '没有可导出的钱包',
        severity: 'warning'
      });
      return;
    }

    try {
      // 创建要导出的内容 - CSV格式（地址,私钥）
      const csvContent = "address,privateKey\n" + wallets
        .filter(wallet => wallet.address && wallet.privateKey)
        .map(wallet => `${wallet.address},${wallet.privateKey}`)
        .join('\n');
      
      // 创建Blob对象
      const blob = new Blob([csvContent], { type: 'text/csv' });
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `钱包地址和私钥_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      
      // 触发下载
      a.click();
      
      // 清理
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      setSnackbar({
        open: true,
        message: `已导出 ${wallets.length} 个钱包地址和私钥为CSV格式`,
        severity: 'success'
      });
    } catch (error) {
      console.error('导出钱包地址出错:', error);
      setSnackbar({
        open: true,
        message: '导出钱包地址失败',
        severity: 'error'
      });
    }
  };

  // 清空所有钱包
  const handleClearAll = () => {
    if (wallets.length === 0) {
      setSnackbar({
        open: true,
        message: '钱包列表已为空',
        severity: 'info'
      });
      return;
    }
    
    onClearAll();
  };

  // 查询余额
  const handleCheckBalance = () => {
    const walletsToCheck = selectedWalletIndices.length > 0 
      ? selectedWalletIndices.map(index => wallets[index])
      : wallets;
    
    if (walletsToCheck.length === 0) {
      setSnackbar({
        open: true,
        message: '请先导入钱包或选择要查询的钱包',
        severity: 'warning'
      });
      return;
    }

    // 获取选中钱包的索引
    const indicesToCheck = walletsToCheck.map(wallet => 
      wallets.findIndex(w => w.privateKey === wallet.privateKey)
    ).filter(index => index !== -1);
    
    onCheckBalance(indicesToCheck);
  };

  // 关闭通知
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 获取状态显示组件
  const getStatusChip = (status?: 'pending' | 'success' | 'error', errorMessage?: string) => {
    if (!status) return null;
    
    switch (status) {
      case 'pending':
        return (
          <Chip
            icon={<HourglassEmptyIcon />}
            label="查询中"
            size="small"
            color="primary"
          />
        );
      case 'success':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="成功"
            size="small"
            color="success"
          />
        );
      case 'error':
        return (
          <Tooltip title={errorMessage || '操作失败'}>
            <Chip
              icon={<ErrorIcon />}
              label="失败"
              size="small"
              color="error"
            />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  // 自定义Toast组件
  interface ToastProps {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    onClose: () => void;
    autoHideDuration?: number;
  }

  const Toast: React.FC<ToastProps> = ({
    open,
    message,
    severity,
    onClose,
    autoHideDuration = 5000
  }) => {
    React.useEffect(() => {
      if (open) {
        const timer = setTimeout(() => {
          onClose();
        }, autoHideDuration);
        return () => clearTimeout(timer);
      }
    }, [open, onClose, autoHideDuration]);

    return (
      <Portal>
        <Fade in={open}>
          <Box
            sx={{
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              minWidth: '300px',
              maxWidth: '80%'
            }}
          >
            <Alert
              severity={severity}
              variant="filled"
              onClose={onClose}
            >
              {message}
            </Alert>
          </Box>
        </Fade>
      </Portal>
    );
  };

  return (
    <Paper sx={{ p: 2, width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          钱包管理
        </Typography>
      </Box>
      
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={1} 
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setIsImportModalOpen(true)}
        >
          导入私钥
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleCheckBalance}
        >
          查询余额
        </Button>
        <Button
          variant="outlined"
          onClick={handleSelectAll}
        >
          {selectedWalletIndices.length === wallets.length ? '取消全选' : '全选'}
        </Button>
        <Button
          variant="outlined"
          onClick={selectWithBalance}
        >
          选择有余额
        </Button>
        <Button
          variant="outlined"
          onClick={selectZeroBalance}
        >
          选择零余额
        </Button>
        <Button
          variant="outlined"
          onClick={selectErrorWallets}
        >
          选择错误
        </Button>
      </Stack>
      
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={1} 
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Button 
          variant="outlined" 
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          disabled={selectedWalletIndices.length === 0}
        >
          删除选中
        </Button>
        <Button 
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={exportWalletAddresses}
        >
          导出钱包地址
        </Button>
        <Button 
          variant="outlined" 
          color="error"
          startIcon={<ClearIcon />}
          onClick={handleClearAll}
        >
          清空所有钱包
        </Button>
      </Stack>
      
      <Divider sx={{ my: 2 }} />
      
      <TableContainer component={Paper}   sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedWalletIndices.length > 0 && selectedWalletIndices.length < wallets.length}
                  checked={wallets.length > 0 && selectedWalletIndices.length === wallets.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell width={80}>序号</TableCell>
              <TableCell>钱包地址</TableCell>
              <TableCell width={120}>BNB余额</TableCell>
              <TableCell width={120}>USDT余额</TableCell>
              <TableCell width={140}>{token1Symbol || 'Token1'}余额</TableCell>
              <TableCell width={140}>{token2Symbol || 'Token2'}余额</TableCell>
              <TableCell width={80}>Nonce</TableCell>
              <TableCell width={100}>状态</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wallets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    还没有钱包，请导入钱包
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              wallets.map((wallet, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    bgcolor: wallet.status === 'error' ? 'error.lighter' : 'inherit'
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedWalletIndices.includes(index)}
                      onChange={() => handleWalletSelection(index)}
                    />
                  </TableCell>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.8rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: { xs: '120px', sm: '200px', md: '300px' }
                    }}>
                      {wallet.address || '未知地址'}
                    </Typography>
                  </TableCell>
                  <TableCell>{wallet.bnbBalance ? _.round(parseFloat(wallet.bnbBalance), 9).toString() : '-'}</TableCell>
                  <TableCell>{wallet.usdtBalance ? _.round(parseFloat(wallet.usdtBalance), 9).toString() : '-'}</TableCell>
                  <TableCell>{wallet.token1Balance ? _.round(parseFloat(wallet.token1Balance), 9).toString() : '-'}</TableCell>
                  <TableCell>{wallet.token2Balance ? _.round(parseFloat(wallet.token2Balance), 9).toString() : '-'}</TableCell>
                  <TableCell>{wallet.nonce ?? '-'}</TableCell>
                  <TableCell>
                    {getStatusChip(wallet.status, wallet.errorMessage)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          总计: {wallets.length} 个钱包
          {selectedWalletIndices.length > 0 && ` | 已选择: ${selectedWalletIndices.length} 个`}
        </Typography>
      </Box>
      
      <ImportModal 
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={onImport}
      />
      
      <Toast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </Paper>
  );
};

export default WalletManagerMUI;
